// Звук через Web Audio API: синтез OscillatorNode + GainNode, без аудиофайлов.
// Пресеты: click, hit, cannon, fanfare, wave, seagull, crown. Запуск — только после первого клика.

export type SoundPreset =
  | 'mail'
  | 'news'
  | 'report'
  | 'click'
  | 'hit'
  | 'cannon'
  | 'fanfare'
  | 'wave'
  | 'seagull'
  | 'crown';

interface Volumes {
  master: number;
  music: number;
  sfx: number;
  /** Звук выключен кнопкой 🔇 (громкость при этом запоминается). */
  muted: boolean;
}

let ctx: AudioContext | null = null;
let volumes: Volumes = loadVolumes();
let musicTimer: number | null = null;

/** Версия настроек звука: при смене звук у всех один раз выключается (новое правило «по умолчанию тихо»). */
const VOLUME_V = 2;

function loadVolumes(): Volumes {
  // По умолчанию звук выключен — игрок включает его сам кнопкой 🔇/🔊 (в игре или на экране входа)
  const defaults: Volumes = { master: 70, music: 30, sfx: 80, muted: true };
  try {
    const saved = JSON.parse(localStorage.getItem('sk_volume') ?? '{}') as Partial<Volumes> & { v?: number };
    if (saved.v !== VOLUME_V) return { ...defaults, ...saved, muted: true };
    return { ...defaults, ...saved };
  } catch {
    return defaults;
  }
}

export function getVolumes(): Volumes {
  return volumes;
}

export function setVolumes(v: Partial<Volumes>): void {
  volumes = { ...volumes, ...v };
  localStorage.setItem('sk_volume', JSON.stringify({ ...volumes, v: VOLUME_V }));
  if (volumes.muted || volumes.music === 0 || volumes.master === 0) stopMusic();
  for (const fn of muteListeners) fn(volumes.muted);
}

const muteListeners = new Set<(muted: boolean) => void>();

/** Подписка на включение/выключение звука (кнопка в шапке и настройки профиля). */
export function onMuteChange(fn: (muted: boolean) => void): () => void {
  muteListeners.add(fn);
  return () => muteListeners.delete(fn);
}

export function isMuted(): boolean {
  return volumes.muted;
}

/** Выключить/включить весь звук игры одной кнопкой. */
export function toggleMute(): boolean {
  setVolumes({ muted: !volumes.muted });
  return volumes.muted;
}

/** Автозапуск аудио разрешён браузером только после жеста пользователя. */
export function unlockAudio(): void {
  if (ctx) return;
  const AC =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AC) return;
  ctx = new AC();
}

function gainFor(kind: 'sfx' | 'music'): number {
  if (volumes.muted) return 0;
  return (volumes.master / 100) * (volumes[kind] / 100);
}

function tone(
  freq: number,
  start: number,
  dur: number,
  type: OscillatorType,
  vol: number,
  slideTo?: number,
): void {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, ctx.currentTime + start + dur);
  g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, vol), ctx.currentTime + start + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(ctx.currentTime + start);
  osc.stop(ctx.currentTime + start + dur + 0.05);
}

function noise(start: number, dur: number, vol: number, lowpass: number): void {
  if (!ctx) return;
  const len = Math.floor(ctx.sampleRate * dur);
  const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = lowpass;
  const g = ctx.createGain();
  g.gain.value = vol;
  src.connect(filter).connect(g).connect(ctx.destination);
  src.start(ctx.currentTime + start);
}

export function play(preset: SoundPreset): void {
  if (!ctx || volumes.muted) return;
  const v = gainFor('sfx');
  if (v <= 0) return;
  switch (preset) {
    case 'click':
      tone(880, 0, 0.06, 'triangle', 0.15 * v);
      break;
    case 'hit':
      noise(0, 0.15, 0.5 * v, 1800);
      tone(140, 0, 0.12, 'square', 0.15 * v, 60);
      break;
    case 'cannon':
      noise(0, 0.6, 0.9 * v, 600);
      tone(90, 0, 0.4, 'sine', 0.5 * v, 35);
      break;
    case 'fanfare':
      [523, 659, 784, 1046].forEach((f, i) => tone(f, i * 0.12, 0.25, 'sawtooth', 0.12 * v));
      break;
    case 'wave':
      noise(0, 1.4, 0.25 * v, 500);
      break;
    case 'seagull':
      tone(1400, 0, 0.18, 'sine', 0.12 * v, 900);
      tone(1500, 0.22, 0.2, 'sine', 0.1 * v, 950);
      break;
    case 'mail':
      // Письмо: два коротких звонка, как колокольчик у двери
      tone(1175, 0, 0.14, 'sine', 0.16 * v);
      tone(1568, 0.13, 0.22, 'sine', 0.14 * v);
      break;
    case 'news':
      // Новость: спокойный восходящий сигнал глашатая
      [440, 554, 659].forEach((f, i) => tone(f, i * 0.16, 0.3, 'triangle', 0.12 * v));
      break;
    case 'report':
      // Отчёт: низкий удар барабана и короткий горн
      noise(0, 0.12, 0.35 * v, 900);
      tone(196, 0.02, 0.2, 'square', 0.12 * v, 130);
      tone(392, 0.2, 0.26, 'sawtooth', 0.1 * v);
      break;
    case 'crown':
      // Смена правящего: торжественный аккорд + колокольчики
      [392, 494, 587].forEach((f) => tone(f, 0, 0.9, 'triangle', 0.12 * v));
      [1568, 1976, 2349, 3136].forEach((f, i) => tone(f, 0.3 + i * 0.1, 0.3, 'sine', 0.08 * v));
      break;
  }
}

/** Фоновая «морская» музыка: тихая пентатоника, раз в несколько секунд. */
export function startMusic(): void {
  if (!ctx || musicTimer !== null || volumes.muted) return;
  const notes = [220, 262, 294, 330, 392, 440];
  const loop = () => {
    const v = gainFor('music');
    if (v > 0 && document.visibilityState === 'visible') {
      const n = notes[Math.floor(Math.random() * notes.length)] ?? 220;
      tone(n, 0, 1.6, 'sine', 0.05 * v);
      if (Math.random() < 0.15) play('wave');
    }
  };
  musicTimer = window.setInterval(loop, 2400);
}

export function stopMusic(): void {
  if (musicTimer !== null) window.clearInterval(musicTimer);
  musicTimer = null;
}
